import { combineLatest, first, of, Subscription, switchMap, take, takeUntil } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';

import { PageMeta, PageMetaService, ProductService, WindowRef } from '@spartacus/core';
import { SdPushSliderComponent } from '@seb/web-design-system/push-slider';

import {
  BrandsEnum,
  ComponentWrapperWidthEnum,
  DeviceService,
  IExtendedLink,
  ILinkMedia,
  IPushCardWithLayout,
  ISliderInfo,
  OrientationEnum,
  ScrollSpyService,
  SdSpyTargetDirective,
  SizesEnum,
  ThemesEnum
} from '@seb/web-design-system/core';

import {
  CmsModuleComponent,
  CustomMedia,
  PushCardComponentInterface,
  PushCardSizeEnum,
  PushCardSliderModuleComponentInterface,
  pushCardWithLayoutSkeleton
} from '../../../models';

import { ChatService, DatalayerService, HobStateService, ImageService, TrackingService } from '../../../services';
import { PushCardMapper, transformHeadlineLevelToTitleLevel } from '../../../mappers';
import { AbstractModuleComponent } from '../abstract-module.component';
import { AppTypeEnum, ProductScopeEnum } from '../../../enums';
import { WrapperComponent } from '../../static/core/wrapper/wrapper.component';

@Component({
  selector: 'wc-push-card-slider-module',
  templateUrl: './push-card-slider-module.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    WrapperComponent,
    SdPushSliderComponent,
    SdSpyTargetDirective
  ]
})
export class PushCardSliderModuleComponent extends AbstractModuleComponent<PushCardSliderModuleComponentInterface> implements OnInit, OnDestroy {
  titleLevelHeading: number;
  pushs: IPushCardWithLayout[];
  pushsSkeleton: IPushCardWithLayout[];
  pushSize: SizesEnum = SizesEnum.m;
  themesEnum = ThemesEnum;
  sizesEnum = SizesEnum;
  isPromos: boolean[] = [];
  isDisplayedPushsPromos: boolean[] = [];
  displayedPushs: IPushCardWithLayout[];
  activeSlider = 0;
  isPushSliderHasBeenViewed = false;
  context: string;
  showMoreDesktop: boolean;
  isReady = false;
  componentWrapperWidthEnum = ComponentWrapperWidthEnum;
  isMobile$ = false;
  isHob = false;
  activeBrand: BrandsEnum;

  @ViewChild('pushSliderComponent', { read: ElementRef })
  pushSliderRef: ElementRef;

  @ViewChild(SdPushSliderComponent)
  pushSliderComponent: SdPushSliderComponent;

  private nbPush: number;
  private nbPushLoaded: number;
  private mediaContainerSubscriptions: Subscription[] = [];

  constructor(
    private pushCardMapper: PushCardMapper,
    private cdRef: ChangeDetectorRef,
    private trackingService: TrackingService,
    private scrollSpyService: ScrollSpyService,
    private datalayerService: DatalayerService,
    private windowRef: WindowRef,
    private imageService: ImageService,
    private pageMetaService: PageMetaService,
    private productService: ProductService,
    private chatService: ChatService,
    private deviceService: DeviceService,
    private readonly hobStateService: HobStateService
  ) {
    super();
  }

  override onModuleInit(_module: CmsModuleComponent) {
    if (this.useLazyLoad) {
      this.pushsSkeleton = [pushCardWithLayoutSkeleton, pushCardWithLayoutSkeleton];
      this.pushs = [pushCardWithLayoutSkeleton, pushCardWithLayoutSkeleton];
    } else {
      this.pushs = [];
    }
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.hobStateService.hobState$.subscribe(({ isHob, liveBrand, siteBrand }) => {
        this.activeBrand = liveBrand || siteBrand;
        this.isHob = isHob;
        this.cdRef.markForCheck();
      })
    );

    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((isMobile) => {
        this.isMobile$ = isMobile;
        this.cdRef.markForCheck();
      })
    );

    this.subscriptions.add(
      combineLatest([this.module$, this.pageMetaService.getMeta()]).pipe(
        switchMap(([module, pageMetaData]) => {
          return combineLatest([
            this.componentService.getSubComponentsLazyComponentData(module, 'componentsObjects', 'links'),
            of(pageMetaData)
          ]);
        })
      ).subscribe(([module, pageMetaData]: [PushCardSliderModuleComponentInterface, PageMeta]) => {
        this.showMoreDesktop = this.wrapper?.layouts?.toLowerCase() === OrientationEnum.column;

        this.context = this.wrapper?.wrapperHeading?.headline
          ? `${pageMetaData.title} ${this.wrapper?.wrapperHeading?.headline}`
          : pageMetaData.title;

        this.titleLevelHeading = module.wrapperHeading?.headlineLevel
          ? transformHeadlineLevelToTitleLevel(module.wrapperHeading?.headlineLevel)
          : 2;

        const size = this.getSliderPushSize(module.componentsObjects);
        this.pushSize = this.mapPushSize(size);

        const pushComponents = module.componentsObjects;
        if (pushComponents.length === 0) {
          this.setLoaded();
        }

        this.nbPush = pushComponents.length;
        this.pushs.splice(this.nbPush);
        this.nbPushLoaded = 0;

        pushComponents.forEach((cmp, index) => {
          if (cmp.mediaContainer) {
            this.mediaContainerSubscriptions[index]?.unsubscribe();
            this.mediaContainerSubscriptions[index] = this.imageService
              .getMediaFromContainer(cmp.mediaContainer)
              .pipe(takeUntil(this.destroy$))
              .subscribe((media) => {
                if (cmp.linksObjects) {
                  this.updateUrlAndAddPush(index, cmp, media);
                } else {
                  this.addPush(index, cmp, media);
                }
              });
          } else {
            if (cmp.linksObjects) {
              this.updateUrlAndAddPush(index, cmp);
            } else {
              this.addPush(index, cmp);
            }
          }
        });

        module.componentsObjects.forEach((componentsObject) =>
          this.isPromos.push(componentsObject.isPushPromo ? JSON.parse(componentsObject.isPushPromo) : false)
        );
        if (isPlatformBrowser(this.platformId)) {
          if (this.windowRef.isBrowser) {
            this.displayedPushs = this.trackingService.pushDisplayedInSlider(
              this.pushSize,
              this.pushs,
              this.activeSlider
            );
            this.isDisplayedPushsPromos = this.trackingService.pushDisplayedInSlider(
              this.pushSize,
              this.isPromos,
              this.activeSlider
            );
          }
        }
      })
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    this.mediaContainerSubscriptions.forEach((s) => s.unsubscribe());
  }

  private getSliderPushSize(pushs: PushCardComponentInterface[]): PushCardSizeEnum {
    return pushs.length > 0 ? (pushs[0].pushSize || PushCardSizeEnum.L) : undefined;
  }

  private mapPushSize(pushSize: PushCardSizeEnum): SizesEnum {
    switch (pushSize) {
      case PushCardSizeEnum.L:
        return SizesEnum.l;
      case PushCardSizeEnum.M:
        return SizesEnum.m;
      case PushCardSizeEnum.S:
        return SizesEnum.s;
      case PushCardSizeEnum.XS:
        return SizesEnum.xs;
      case PushCardSizeEnum.XXS:
        return SizesEnum.xxs;
      default:
        return SizesEnum.default;
    }
  }

  private updateUrlAndAddPush(index: number, cmp: PushCardComponentInterface, media?: CustomMedia) {
    const urlLocalized = cmp.linksObjects[0]?.urlLocalized;
    if (urlLocalized?.includes('/p/')) {
      const code = urlLocalized.split('/p/')[1];
      this.productService
        .hasError(code, ProductScopeEnum.CARD)
        .pipe(
          switchMap((err) => {
            if (err) {
              return of(null);
            }
            if (code) {
              return this.productService.get(code, ProductScopeEnum.CARD).pipe(first((p) => !!p));
            } else {
              return of(null);
            }
          })
        )
        .pipe(take(1))
        .subscribe((product) => {
          if (product && cmp?.linksObjects.length > 0) {
            cmp.linksObjects[0].urlLocalized = product?.url;
          }
          if (urlLocalized.startsWith('/p/')) {
            cmp.linksObjects = urlLocalized.endsWith('/p/') ? null : cmp.linksObjects;
            this.handleAddPush(index, cmp, media);
          }
          this.handleAddPush(index, cmp, media);
        });
    } else {
      this.handleAddPush(index, cmp, media);
    }
  }

  private handleAddPush(index, cmp, media) {
    if (media) {
      this.addPush(index, cmp, media);
    } else {
      this.addPush(index, cmp);
    }
  }

  onClickLink(event: { link: IExtendedLink | ILinkMedia, isBrandCorner: boolean }): void {
    if (event?.link?.url === '<<chat>>') {
      this.chatService.clickOpenChat();
    }
    if (event?.['isBrandCorner']) {
      if (this.isHob) {
        this.trackingService.sendEvent('clickCrossBrand', {
          productCategory: event?.link.url,
          positionLabel: 'section discover all our product range'
        });
      } else {
        this.trackingService.sendEvent('clickBrandCorner', {
          productCategory: event?.link.url,
          positionLabel: 'section'
        });
      }
    }
    this.isPromos.forEach((isPromo, index) => {
      if (isPromo) {
        if (this.displayedPushs[index].push.richParagraph.links[0].url === event?.link?.url) {
          const promotionName: string = this.displayedPushs[index].push.richParagraph.heading.data.title;
          this.trackingService.sendEvent('promotionClick', {
            promotionName
          });
          this.datalayerService.setDataLayerInternalPromotion(promotionName);
        }
      }
    });

    let appType: string;
    for (const app in AppTypeEnum) {
      if (event?.link?.url.includes(app)) {
        appType = AppTypeEnum[app];
        break;
      }
    }

    if (appType) {
      this.trackingService.sendEvent('clickdownloadApp', {
        appType
      });
    } else {
      this.trackingService.sendEvent('clickCardFrames', {
        componentName: event?.link?.linkName
      });
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.windowRef.isBrowser) {
      this.displayedPushs = this.trackingService.pushDisplayedInSlider(this.pushSize, this.pushs, this.activeSlider);
      this.isDisplayedPushsPromos = this.trackingService.pushDisplayedInSlider(
        this.pushSize,
        this.isPromos,
        this.activeSlider
      );
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (
      !this.isPushSliderHasBeenViewed &&
      !!this.displayedPushs &&
      this.scrollSpyService.isElementActive(this.pushSliderRef)
    ) {
      this.isPushSliderHasBeenViewed = true;
      this.displayedPushs.forEach((displayedPush: IPushCardWithLayout, index) => {
        if (this.isDisplayedPushsPromos[index]) {
          const promotionName: string = displayedPush.push.richParagraph.heading.data.title;
          this.trackingService.sendEvent('promotionView', {
            promotionName
          });
          this.datalayerService.setDataLayerInternalPromotion(promotionName);
        }
      });
    }
  }

  onSlide(event: ISliderInfo): void {
    this.activeSlider = event.index;
    if (this.windowRef.isBrowser) {
      this.displayedPushs = this.trackingService.pushDisplayedInSlider(this.pushSize, this.pushs, this.activeSlider);
      this.isDisplayedPushsPromos = this.trackingService.pushDisplayedInSlider(
        this.pushSize,
        this.isPromos,
        this.activeSlider
      );
    }

    this.displayedPushs.forEach((displayedPush: IPushCardWithLayout, index) => {
      if (this.isDisplayedPushsPromos[index]) {
        const promotionName: string = displayedPush.push.richParagraph.heading.data.title;
        this.trackingService.sendEvent('promotionView', {
          promotionName
        });
        this.datalayerService.setDataLayerInternalPromotion(promotionName);
      }
    });
  }

  private addPush(index: number, cmp: PushCardComponentInterface, media?: CustomMedia) {
    const brand = this.baseStoreService.getBrand(cmp['brandCorner']);
    const brandCorner = brand ? brand.id as BrandsEnum : BrandsEnum.default;
    const customMedia = media ? this.imageService.normalizeMedia(media) : undefined;

    if (cmp.linksObjects && cmp.linksObjects[0]?.urlLocalized?.includes('/p/')) {
      const code = cmp.linksObjects[0].urlLocalized.split('/').pop();
      this.productService
        .hasError(code, ProductScopeEnum.CARD)
        .pipe(
          switchMap((err) => {
            if (err) {
              return of(null);
            }
            return this.productService.get(code, ProductScopeEnum.CARD).pipe(first((p) => !!p));
          })
        )
        .pipe(take(1))
        .subscribe((product) => {
          const newCmp = {
            ...cmp,
            indexFollow: product?.indexFollow,
            brandCorner
          };
          this.pushs[index] = this.pushCardMapper.toPushCardWithLayout(newCmp, customMedia, this.isMobile$);
        });
    } else {
      const newCmp = {
        ...cmp,
        brandCorner
      };
      this.pushs[index] = this.pushCardMapper.toPushCardWithLayout(newCmp, customMedia, this.isMobile$);
    }
    this.nbPushLoaded++;
    if (this.nbPushLoaded === this.nbPush) {
      this.setLoaded();
      this.isReady = true;
      this.cdRef.markForCheck();
    }
  }
}
